// The 66 books, by OSIS id, with the names and abbreviations readers type. Used to parse references
// ("rom 8 28", "1 Jn 1:9", "Song of Songs 2") into OSIS keys and labels.
type Book = { osis: string; name: string; /** The name in a reference: "Psalm 46:1", not "Psalms 46:1". */ ref: string; aliases: string[] };

const B = (osis: string, name: string, aliases: string[], ref = name): Book => ({ osis, name, ref, aliases });

export const BOOKS: Book[] = [
  B("Gen", "Genesis", ["gn", "ge"]),
  B("Exod", "Exodus", ["ex", "exo"]),
  B("Lev", "Leviticus", ["lv", "le"]),
  B("Num", "Numbers", ["nm", "nu"]),
  B("Deut", "Deuteronomy", ["dt", "deu"]),
  B("Josh", "Joshua", ["jos", "jsh"]),
  B("Judg", "Judges", ["jdg", "jg", "jdgs"]),
  B("Ruth", "Ruth", ["rt", "ru"]),
  B("1Sam", "1 Samuel", ["1sa", "1sm", "1s"]),
  B("2Sam", "2 Samuel", ["2sa", "2sm", "2s"]),
  B("1Kgs", "1 Kings", ["1ki", "1kg", "1k"]),
  B("2Kgs", "2 Kings", ["2ki", "2kg", "2k"]),
  B("1Chr", "1 Chronicles", ["1ch", "1chron"]),
  B("2Chr", "2 Chronicles", ["2ch", "2chron"]),
  B("Ezra", "Ezra", ["ezr"]),
  B("Neh", "Nehemiah", ["ne"]),
  B("Esth", "Esther", ["est", "es"]),
  B("Job", "Job", ["jb"]),
  B("Ps", "Psalms", ["ps", "psa", "pss", "psalm", "psalms", "psm"], "Psalm"),
  B("Prov", "Proverbs", ["pr", "prv", "pro"]),
  B("Eccl", "Ecclesiastes", ["ec", "ecc", "eccles", "qoh"]),
  B("Song", "Song of Songs", ["sng", "sos", "songofsolomon", "canticles", "song"]),
  B("Isa", "Isaiah", ["is"]),
  B("Jer", "Jeremiah", ["je", "jr"]),
  B("Lam", "Lamentations", ["la"]),
  B("Ezek", "Ezekiel", ["ezk", "eze"]),
  B("Dan", "Daniel", ["dn", "da"]),
  B("Hos", "Hosea", ["ho"]),
  B("Joel", "Joel", ["jl", "joe"]),
  B("Amos", "Amos", ["am"]),
  B("Obad", "Obadiah", ["ob", "oba"]),
  B("Jonah", "Jonah", ["jnh", "jon"]),
  B("Mic", "Micah", ["mi"]),
  B("Nah", "Nahum", ["na"]),
  B("Hab", "Habakkuk", ["hb"]),
  B("Zeph", "Zephaniah", ["zep", "zp"]),
  B("Hag", "Haggai", ["hg"]),
  B("Zech", "Zechariah", ["zec", "zc"]),
  B("Mal", "Malachi", ["ml"]),
  B("Matt", "Matthew", ["mt", "mat"]),
  B("Mark", "Mark", ["mk", "mrk", "mr"]),
  B("Luke", "Luke", ["lk", "luk"]),
  B("John", "John", ["jn", "jhn", "joh"]),
  B("Acts", "Acts", ["ac", "act"]),
  B("Rom", "Romans", ["ro", "rm"]),
  B("1Cor", "1 Corinthians", ["1co"]),
  B("2Cor", "2 Corinthians", ["2co"]),
  B("Gal", "Galatians", ["ga"]),
  B("Eph", "Ephesians", ["ep"]),
  B("Phil", "Philippians", ["php", "phil", "pp"]),
  B("Col", "Colossians", ["co"]),
  B("1Thess", "1 Thessalonians", ["1th", "1thes"]),
  B("2Thess", "2 Thessalonians", ["2th", "2thes"]),
  B("1Tim", "1 Timothy", ["1ti", "1tm"]),
  B("2Tim", "2 Timothy", ["2ti", "2tm"]),
  B("Titus", "Titus", ["tit", "ti"]),
  B("Phlm", "Philemon", ["phm", "phlm", "philem"]),
  B("Heb", "Hebrews", ["he"]),
  B("Jas", "James", ["jm", "jas"]),
  B("1Pet", "1 Peter", ["1pe", "1pt", "1p"]),
  B("2Pet", "2 Peter", ["2pe", "2pt", "2p"]),
  B("1John", "1 John", ["1jn", "1jhn", "1jo"]),
  B("2John", "2 John", ["2jn", "2jhn", "2jo"]),
  B("3John", "3 John", ["3jn", "3jhn", "3jo"]),
  B("Jude", "Jude", ["jud", "jude"]),
  B("Rev", "Revelation", ["rv", "re", "revelations", "apoc"]),
];

const norm = (s: string) => s.toLowerCase().replace(/[\s.]+/g, "");
const BY_OSIS = new Map(BOOKS.map((b) => [b.osis, b]));
const EXACT = new Map<string, Book>();
for (const b of BOOKS) for (const a of [norm(b.osis), norm(b.name), ...b.aliases]) if (!EXACT.has(a)) EXACT.set(a, b);

/** A book from what a reader typed: an exact name or abbreviation, else the first book whose name starts with it. */
export function findBook(input: string): Book | null {
  const k = norm(input);
  if (!k) return null;
  const exact = EXACT.get(k);
  if (exact) return exact;
  if (k.replace(/^\d/, "").length < 3) return null;
  return BOOKS.find((b) => norm(b.name).startsWith(k)) ?? null;
}

export const bookByOsis = (osis: string) => BY_OSIS.get(osis) ?? null;

export type ParsedRef = { key: string; label: string };
/**
 * `chapter` to `chapterEnd` (the same within one chapter); `from` is the first verse in the first
 * chapter and `to` the last in the last, null for the chapter's own edge.
 */
export type ParsedPassage = ParsedRef & { book: string; chapter: number; chapterEnd: number; from: number | null; to: number | null };

/** A plan's OSIS range: "Rom.1-Rom.2", "Gen.1.20-Gen.2.4", "Gen.17.19-Gen.17.27" (reading plans, 08 §3). */
const OSIS_RANGE = /^([1-3]?[A-Za-z]+)\.(\d{1,3})(?:\.(\d{1,3}))?-([1-3]?[A-Za-z]+)\.(\d{1,3})(?:\.(\d{1,3}))?$/;

function parseOsisRange(m: RegExpMatchArray, maxVerses: number): ParsedPassage | null {
  const [raw, b1, c1, v1, b2, c2, v2] = m;
  const book = bookByOsis(b1!);
  if (!book || b1 !== b2) return null;
  const chapter = Number(c1);
  const chapterEnd = Number(c2);
  const from = v1 ? Number(v1) : null;
  const to = v2 ? Number(v2) : null;
  if (chapter < 1 || chapterEnd < chapter || (from !== null && from < 1) || (to !== null && to < 1)) return null;
  // Within one chapter, the ordinary form: "Gen.17.19-27", "Rom.8".
  if (chapterEnd === chapter && (from === null) === (to === null))
    return parsePassage(from === null ? `${book.osis}.${chapter}` : `${book.osis}.${chapter}.${from}${to! > from ? `-${to}` : ""}`, maxVerses);
  // Across chapters: read, never quoted, so never under a quote's cap.
  if (Number.isFinite(maxVerses)) return null;
  const label =
    from === null && to === null
      ? `${book.ref} ${chapter}–${chapterEnd}`
      : `${book.ref} ${chapter}:${from ?? 1}–${chapterEnd}${to === null ? "" : `:${to}`}`;
  return { key: raw!, label, book: book.osis, chapter, chapterEnd, from, to };
}

const REF = /^([1-3]?\s*[a-z]+(?:\s+of\s+[a-z]+)?)\.?\s*(\d{1,3})(?:\s*[:. ]\s*(\d{1,3})(?:\s*[-–]\s*(\d{1,3}))?)?$/;

/**
 * "Romans 8:28", "rom 8.28", "Phil 4:6-7", "Romans 8", or an OSIS key ("Phil.4.6-7", "Rom.8") →
 * the passage. `maxVerses` caps a range (quotes are capped at five verses, 00 §3).
 */
export function parsePassage(input: string, maxVerses = Infinity): ParsedPassage | null {
  const raw = input.trim();
  const range = raw.match(OSIS_RANGE);
  if (range) return parseOsisRange(range, maxVerses);
  const osis = raw.match(/^([1-3]?[A-Za-z]+)\.(\d{1,3})(?:\.(\d{1,3})(?:-(\d{1,3}))?)?$/);
  const m = osis && bookByOsis(osis[1]!) ? osis : raw.toLowerCase().match(REF);
  if (!m) return null;
  const [, rawBook, ch, v1, v2] = m;
  const book = osis && m === osis ? bookByOsis(rawBook!) : findBook(rawBook!);
  if (!book) return null;
  const chapter = Number(ch);
  if (chapter < 1) return null;
  if (!v1) return { key: `${book.osis}.${chapter}`, label: `${book.ref} ${chapter}`, book: book.osis, chapter, chapterEnd: chapter, from: null, to: null };
  const from = Number(v1);
  const to = v2 && Number(v2) > from ? Number(v2) : null;
  if (from < 1) return null;
  if (to !== null && to - from + 1 > maxVerses) return null;
  const verses = to ? `${from}-${to}` : `${from}`;
  return {
    key: `${book.osis}.${chapter}.${verses}`,
    label: `${book.ref} ${chapter}:${to ? `${from}–${to}` : from}`,
    book: book.osis,
    chapter,
    chapterEnd: chapter,
    from,
    to,
  };
}
